import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, type Me, type Membership, type OrgApi, type Role } from "./api";

interface SessionValue {
  me: Me | null;
  loading: boolean;
  /** true when the control plane could not be reached (vs. simply signed out) */
  offline: boolean;
  refresh: () => Promise<Me | null>;
  signOut: () => Promise<void>;
  membership: Membership | null;
  setOrgId: (orgId: string) => void;
}

const SessionContext = createContext<SessionValue | null>(null);
const ORG_KEY = "dbi.currentOrg";

function readOrgPref(): string | null {
  try {
    return localStorage.getItem(ORG_KEY);
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [orgId, setOrgIdState] = useState<string | null>(readOrgPref);

  const refresh = useCallback(async () => {
    try {
      const m = await api.auth.me();
      setMe(m);
      setOffline(false);
      return m;
    } catch (e) {
      setMe(null);
      setOffline(e instanceof ApiError && e.status === 0);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      setMe(null);
    }
  }, []);

  const setOrgId = useCallback((id: string) => {
    setOrgIdState(id);
    try {
      localStorage.setItem(ORG_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  // The org preference is only a hint; it is validated against server-provided memberships.
  const membership = useMemo(() => {
    if (!me || me.memberships.length === 0) return null;
    return me.memberships.find((m) => m.orgId === orgId) ?? me.memberships[0]!;
  }, [me, orgId]);

  const value = useMemo(() => ({ me, loading, offline, refresh, signOut, membership, setOrgId }), [me, loading, offline, refresh, signOut, membership, setOrgId]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}

/** Current org API + role helpers. Only valid inside authenticated routes. */
export function useOrg(): { orgId: string; orgName: string; role: Role; kinds: string[]; client: OrgApi; can: (perm: Permission) => boolean } {
  const { membership } = useSession();
  if (!membership) throw new Error("useOrg requires an active membership");
  const client = useMemo(() => api.org(membership.orgId), [membership.orgId]);
  return {
    orgId: membership.orgId,
    orgName: membership.orgName,
    role: membership.role,
    kinds: membership.kinds,
    client,
    can: (perm) => PERMISSIONS[perm].includes(membership.role),
  };
}

/** Mirrors the server RBAC matrix (INTERFACES §7). UI hint only — the server is authoritative. */
export type Permission = "approveContract" | "activateDataset" | "piiOverride" | "pricing" | "apiKeys" | "billing" | "members" | "editSources";
export const PERMISSIONS: Record<Permission, Role[]> = {
  approveContract: ["owner", "admin", "security_reviewer"],
  activateDataset: ["owner", "admin"],
  piiOverride: ["owner", "security_reviewer"],
  pricing: ["owner", "admin", "billing_admin"],
  apiKeys: ["owner", "admin", "developer"],
  billing: ["owner", "billing_admin"],
  members: ["owner", "admin"],
  editSources: ["owner", "admin", "data_engineer"],
};
